/**
 * AlertsDashboard Component
 * Alerts tab showing active alerts sorted by severity,
 * with acknowledge buttons and collapsible threshold settings.
 * Owns its own data fetching via useAlerts hook (only fires when tab is active).
 */

import { useState, useCallback, useEffect } from 'react';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { CommentButton } from './CommentButton';
import { PageNotes } from './PageNotes';
import { useAlerts } from '@/hooks/useAlerts';
import { formatCurrency, formatRelativeTime } from '@/utils/formatters';
import './AlertsDashboard.css';

interface AlertsDashboardProps {
  onAlertCountChange?: (count: number) => void;
}

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

function severityToStatus(severity: string): 'critical' | 'warning' | 'info' {
  if (severity === 'critical') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function operatorLabel(operator: string): string {
  const labels: Record<string, string> = {
    lt: 'less than',
    lte: 'at most',
    gt: 'greater than',
    gte: 'at least',
    eq: 'equals',
  };
  return labels[operator] ?? operator;
}

export function AlertsDashboard({ onAlertCountChange }: AlertsDashboardProps) {
  const { alerts, thresholds, loading, error, activeCount, refresh, acknowledge } = useAlerts();
  const [acknowledging, setAcknowledging] = useState<string | null>(null);

  // Report active count to parent for tab badge
  useEffect(() => {
    onAlertCountChange?.(activeCount);
  }, [activeCount, onAlertCountChange]);

  const handleAcknowledge = useCallback(
    async (alertId: string) => {
      try {
        setAcknowledging(alertId);
        await acknowledge(alertId);
      } finally {
        setAcknowledging(null);
      }
    },
    [acknowledge]
  );

  if (error) {
    return (
      <div className="dashboard-error" role="alert" aria-live="assertive">
        <div className="dashboard-error__icon">&#9888;</div>
        <h2 className="dashboard-error__title">Unable to load Alerts</h2>
        <p className="dashboard-error__message">{error}</p>
        <button onClick={refresh} className="dashboard-error__button" aria-label="Retry">
          Try Again
        </button>
      </div>
    );
  }

  const sortedAlerts = [...alerts].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3)
  );

  const activeAlerts = sortedAlerts.filter((a) => a.status === 'active');
  const acknowledgedAlerts = sortedAlerts.filter((a) => a.status === 'acknowledged');

  return (
    <div className="alerts-dashboard">
      <header className="alerts-dashboard__header">
        <div>
          <h1 className="alerts-dashboard__title">Alerts</h1>
          <p className="alerts-dashboard__subtitle">
            {activeAlerts.length} active{' '}
            {activeAlerts.length === 1 ? 'alert' : 'alerts'}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="dashboard-header__refresh"
          aria-label="Refresh alerts"
        >
          <span className={loading ? 'loading-spinner' : ''}>&#8635;</span>
          Refresh
        </button>
      </header>

      {/* Loading state */}
      {loading && alerts.length === 0 && (
        <div className="alerts-dashboard__loading">
          <div className="loading-spinner" />
          <p>Loading alerts...</p>
        </div>
      )}

      {/* No alerts */}
      {!loading && alerts.length === 0 && (
        <div className="alerts-dashboard__empty-state">
          <div className="alerts-dashboard__empty-icon">&#10003;</div>
          <h2 className="alerts-dashboard__empty-title">All clear</h2>
          <p className="alerts-dashboard__empty-message">
            No alerts have been triggered. Your financial metrics are within thresholds.
          </p>
        </div>
      )}

      {/* Active Alerts */}
      {activeAlerts.length > 0 && (
        <div className="alerts-dashboard__section" aria-label="Active alerts">
          {activeAlerts.map((alert) => (
            <article
              key={alert.id}
              className={`alerts-dashboard__card alerts-dashboard__card--${alert.severity}`}
              role="alert"
            >
              <div className="alerts-dashboard__card-header">
                <StatusBadge status={severityToStatus(alert.severity)} />
                <span className="alerts-dashboard__time">
                  {formatRelativeTime(alert.triggeredAt)}
                </span>
                <span onClick={(e) => e.stopPropagation()} role="presentation">
                  <CommentButton targetType="metric" targetId={`alert-${alert.id}`} />
                </span>
              </div>
              <p className="alerts-dashboard__message">{alert.message}</p>
              <div className="alerts-dashboard__values">
                <span className="alerts-dashboard__value-label">Current:</span>
                <span className="alerts-dashboard__value-amount">
                  {formatCurrency(alert.currentValue)}
                </span>
                <span className="alerts-dashboard__value-label">Threshold:</span>
                <span className="alerts-dashboard__value-amount">
                  {formatCurrency(alert.thresholdValue)}
                </span>
              </div>
              <button
                className="alerts-dashboard__acknowledge"
                onClick={() => handleAcknowledge(alert.id)}
                disabled={acknowledging === alert.id}
                aria-label={`Acknowledge alert: ${alert.message}`}
              >
                {acknowledging === alert.id ? 'Acknowledging...' : 'Acknowledge'}
              </button>
            </article>
          ))}
        </div>
      )}

      {/* Acknowledged Alerts */}
      {acknowledgedAlerts.length > 0 && (
        <SectionCard
          title="Acknowledged"
          badge={
            <span className="alerts-dashboard__count">{acknowledgedAlerts.length}</span>
          }
        >
          <div className="alerts-dashboard__acknowledged-list">
            {acknowledgedAlerts.map((alert) => (
              <div key={alert.id} className="alerts-dashboard__acknowledged-item">
                <StatusBadge status="neutral" label="acknowledged" size="sm" />
                <span className="alerts-dashboard__acknowledged-message">{alert.message}</span>
                <span className="alerts-dashboard__acknowledged-time">
                  {alert.acknowledgedAt ? formatRelativeTime(alert.acknowledgedAt) : ''}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Page Notes */}
      <PageNotes page="alerts" title="Alerts Notes" />

      {/* Threshold Settings */}
      <SectionCard title="Threshold Settings">
        {thresholds.length > 0 ? (
          <div className="alerts-dashboard__thresholds">
            {thresholds.map((threshold) => (
              <div
                key={threshold.id}
                className={`alerts-dashboard__threshold ${!threshold.enabled ? 'alerts-dashboard__threshold--disabled' : ''}`}
              >
                <div className="alerts-dashboard__threshold-info">
                  <span className="alerts-dashboard__threshold-desc">
                    {threshold.description}
                  </span>
                  <span className="alerts-dashboard__threshold-rule">
                    {threshold.metric.replace(/_/g, ' ')} {operatorLabel(threshold.operator)}{' '}
                    {formatCurrency(threshold.value)}
                  </span>
                </div>
                <StatusBadge
                  status={severityToStatus(threshold.severity)}
                  label={threshold.severity}
                  size="sm"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="alerts-dashboard__empty">No thresholds configured</p>
        )}
      </SectionCard>
    </div>
  );
}
